import { useEffect, useState } from "react";
import { Team } from "../../types/team";
import { TeamCreate } from "./component/team-create";
import { teamApi } from "./team-api";
import { toast } from "react-toastify";
import PermissionGuard from "../../components/PermissionGuard";

export const TeamList = () => {

    const [teams, setTeams] = useState<Team[]>([])
    const [isShowTeamCreate, setShowTeamCreate] = useState(false)
    const [isShowTeamEdit, setShowTeamEdit] = useState(false)
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)

    const [isLoading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null);

    const fetchTeams = async () => {
        try {
            setLoading(true);
            const data = await teamApi.getAllTeams() as any;
            setTeams(data.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    function getTeamLeader(team: Team) {
        return team.members?.find(t => t.role == 1)
    }

    function handleTeamEdit(team: Team): void {
        setSelectedTeam(team)
        setShowTeamEdit(true)
        setShowTeamCreate(false)

    }

    function handleCreateEditClose(): void {
        setShowTeamEdit(false)
        setShowTeamCreate(false)
        fetchTeams()
    }

    const handleTeamDelete = async (team: Team) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete Team "${team.name}" with ${team.members?.length || 0} member(s)?`);
        if (isConfirmed) {
            try {
                await teamApi.deleteTeam(team.id);
                toast.success(`Team "${team.name}" deleted successfully!`);
                fetchTeams();
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to delete team';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
            <div className="max-w-[1600px] mx-auto">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Team Management</h1>
                            <p className="text-gray-600 dark:text-gray-300">Organize and manage your teams efficiently</p>
                        </div>
                        <button 
                            onClick={() => setShowTeamCreate(!isShowTeamCreate)} 
                            className="btn-primary flex items-center gap-2 px-6 py-3 shadow-lg"
                        >
                            <i className="fa-solid fa-plus"></i>
                            <span>Create New Team</span>
                        </button>
                    </div>

                    {/* Filters Section */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6 mb-6">
                        <div className="flex flex-col md:flex-row items-center gap-4">
                            <div className="flex-1 w-full">
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors" 
                                        placeholder="Search teams..." 
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                                        <i className="fa-solid fa-search"></i>
                                    </span>
                                </div>
                            </div>
                            <div className="w-full md:w-auto">
                                <select className="w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors">
                                    <option value="">All Areas</option>
                                    <option value="treasury">Treasury</option>
                                    <option value="events">Event Organization</option>
                                    <option value="operations">Operations</option>
                                    <option value="marketing">Marketing</option>
                                    <option value="hr">Human Resources</option>
                                    <option value="it">IT Support</option>
                                </select>
                            </div>
                            <div className="w-full md:w-auto">
                                <select className="w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors">
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-circle-exclamation text-red-600 dark:text-red-400"></i>
                            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="flex flex-col items-center gap-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                            <p className="text-gray-600 dark:text-gray-300">Loading teams...</p>
                        </div>
                    </div>
                ) : teams.length === 0 ? (
                    <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                <i className="fa-solid fa-users text-3xl text-gray-400 dark:text-gray-600"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">No teams found</h3>
                            <p className="text-gray-600 dark:text-gray-400">Get started by creating your first team</p>
                            <PermissionGuard permission={["team.create", "team.manage"]}>
                                <button 
                                    onClick={() => setShowTeamCreate(!isShowTeamCreate)} 
                                    className="btn-primary mt-4 flex items-center gap-2 px-6 py-3"
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    <span>Create New Team</span>
                                </button>
                            </PermissionGuard>
                        </div>
                    </div>
                ) : (
                    /* Teams Grid */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {teams.map(team => {
                            const leader = getTeamLeader(team);
                            return (
                                <div 
                                    key={team.id} 
                                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 overflow-hidden"
                                >
                                    <div className="p-6">
                                        {/* Header */}
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{team.name}</h3>
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                    <i className="fa-solid fa-circle text-[6px] mr-2"></i>
                                                    Active
                                                </span>
                                            </div>
                                            <div className="flex gap-2">
                                                <PermissionGuard permission={["team.update", "team.manage"]}>
                                                    <button 
                                                        onClick={() => handleTeamEdit(team)}
                                                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        title="Edit team"
                                                    >
                                                        <i className="fa-solid fa-edit"></i>
                                                    </button>
                                                </PermissionGuard>
                                                <PermissionGuard permission={["team.delete", "team.manage"]}>
                                                    <button 
                                                        onClick={() => handleTeamDelete(team)}
                                                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="Delete team"
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </button>
                                                </PermissionGuard>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {team.description && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                                                {team.description}
                                            </p>
                                        )}

                                        {/* Team Leader */}
                                        {leader && (
                                            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                                    Team Leader
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold">
                                                        {leader.user?.name?.charAt(0).toUpperCase() || 'L'}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                            {leader.user?.name || 'No leader assigned'}
                                                        </div>
                                                        {leader.user?.phone && (
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                {leader.user.phone}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Members Section */}
                                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                    Members
                                                </div>
                                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold">
                                                    {team.members?.length || 0}
                                                </span>
                                            </div>
                                            {team.members && team.members.length > 0 ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="flex -space-x-2">
                                                        {team.members.slice(0, 4).map((member, index) => (
                                                            <div
                                                                key={member.id || index}
                                                                className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-semibold"
                                                                title={member.user?.name}
                                                            >
                                                                {member.user?.name?.charAt(0).toUpperCase() || 'M'}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {team.members.length > 4 && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                                            +{team.members.length - 4} more
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">No members yet</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Modals */}
                {isShowTeamCreate && <TeamCreate onClose={() => handleCreateEditClose()} isCreate={true} />}
                {isShowTeamEdit && selectedTeam != null && <TeamCreate onClose={() => handleCreateEditClose()} team={selectedTeam} isCreate={false} />}
            </div>
        </div>
    );
}